/* This is an entrypoint for multi-select in the content tree. It opens the BulkActionsMenu, which can be found in @confluence/page-tree */
import React, { useEffect, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import TasksIcon from '@atlaskit/icon/core/tasks';
import { Pressable } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { BulkActionsChangeboardingSpotlight } from '@confluence/page-tree/entry-points/BulkActionsChangeboardingSpotlight.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expVal } from '@confluence/feature-experiments';

type MultiSelectButtonProps = {
	onClick: () => void;
	isDisabled: boolean;
	spaceHasUserContent: boolean;
	isMultiSelectEnabled: boolean;
	isLoading: boolean;
	isMobile: boolean;
	spaceKey: string;
};

export const MultiSelectButton = ({
	onClick,
	isDisabled,
	spaceHasUserContent,
	isMultiSelectEnabled,
	isLoading,
	isMobile,
	spaceKey,
}: MultiSelectButtonProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredScreenEvent = useRef(false);

	// only fire the screen event once when the sidebar is expanded
	useEffect(() => {
		const tryFireScreenEvent = () => {
			if (
				localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED) ===
					'expanded' &&
				!hasFiredScreenEvent.current
			) {
				hasFiredScreenEvent.current = true;
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'multiSelectButton',
					},
				}).fire();
			}
		};

		tryFireScreenEvent();

		window.addEventListener('resize', tryFireScreenEvent);
		return () => {
			window.removeEventListener('resize', tryFireScreenEvent);
		};
	}, [createAnalyticsEvent]);

	if (!spaceHasUserContent || isMobile || !isMultiSelectEnabled) {
		return null;
	}

	if (expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false)) {
		return (
			<BulkActionsChangeboardingSpotlight spaceKey={spaceKey}>
				<IconButton
					icon={TasksIcon}
					label={intl.formatMessage(i18n.enableMultiSelect)}
					appearance="subtle"
					spacing="compact"
					onClick={onClick}
					isTooltipDisabled={false}
					isDisabled={isLoading || isDisabled}
					tooltip={{
						content: isDisabled
							? intl.formatMessage(i18n.disabledMultiSelectTooltipOnSearch)
							: undefined,
					}}
				/>
			</BulkActionsChangeboardingSpotlight>
		);
	}

	return (
		<IconButton
			icon={TasksIcon}
			label={intl.formatMessage(i18n.enableMultiSelect)}
			appearance="subtle"
			spacing="compact"
			onClick={onClick}
			isTooltipDisabled={false}
			isDisabled={isLoading || isDisabled}
			tooltip={{
				content: isDisabled
					? intl.formatMessage(i18n.disabledMultiSelectTooltipOnSearch)
					: undefined,
			}}
		/>
	);
};

type ExitMultiSelectButtonProps = {
	onClick: () => void;
};

export const ExitMultiSelectButton = ({ onClick }: ExitMultiSelectButtonProps) => {
	const intl = useIntl();

	return (
		<Pressable
			aria-label={intl.formatMessage(i18n.exitMultiSelectLabel)}
			onClick={onClick}
			xcss={styles.exit}
			analyticsContext={{
				source: 'contentTreeBulkActions',
				attributes: {
					name: 'exitMultiSelectButton',
				},
			}}
		>
			{intl.formatMessage(i18n.exitMultiSelect)}
		</Pressable>
	);
};

const i18n = defineMessages({
	exitMultiSelect: {
		id: 'space-views.exit-multi-select',
		defaultMessage: 'Exit',
		description: 'Text to exit the multi-select mode and exit bulk actions in the content tree',
	},
	exitMultiSelectLabel: {
		id: 'space-views.exit-multi-select-label',
		defaultMessage: 'Exit multi-select mode',
		description: 'Label for the button to exit multi-select mode in the content tree',
	},
	enableMultiSelect: {
		id: 'space-views.enable-multi-select',
		defaultMessage:
			'Select multiple items to edit. Only items you have permission to modify will be affected.',
		description: 'Text to enable multi-select mode in the content tree',
	},
	disabledMultiSelectTooltipOnSearch: {
		id: 'space-views.disabled-multi-select-tooltip',
		defaultMessage: 'Multi-select is disabled when searching',
		description: 'Tooltip for the disabled multi-select button when search is active',
	},
});

const styles = cssMap({
	exit: {
		zIndex: 1,
		height: '20px',
		font: token('font.body'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.selected'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});
